import { useCallback, useEffect, useId, useRef, useState, type MouseEvent } from 'react'
import { Link } from 'react-router'

import { cn, focusRing, insetFocusRing } from './cn'
import { isPlainClick } from './events'
import { ChevronDown, ChevronUp, Moon, Sun, type IconComponent } from './icons'

/** A menu entry: a link, or an action such as signing out. */
export type AccountMenuItem =
  | { readonly label: string; readonly href: string }
  | { readonly label: string; readonly onSelect: () => void }

export type ColorMode = 'light' | 'dark'

/** The theme choice at the foot of the menu, with its labels. */
export interface ColorModeChoice {
  readonly label: string
  readonly lightLabel: string
  readonly darkLabel: string
  readonly mode: ColorMode
  readonly onChange: (mode: ColorMode) => void
}

export interface AccountMenuProps {
  /** The trigger's text, such as the person's name. */
  readonly name: string
  readonly items: readonly AccountMenuItem[]
  readonly colorMode?: ColorModeChoice
}

const itemClass = cn(
  'block w-full cursor-pointer px-4 py-2 text-left text-sm text-text-primary transition hover:bg-background-contrast-sm',
  insetFocusRing,
)

/**
 * The account dropdown at the right of the primary nav: the trigger in `primary-main`, then
 * the items, then the Light and Dark tiles. It is a disclosure: Tab moves through it, and
 * Escape, a click outside or focus moving outside closes it.
 */
export function AccountMenu({ name, items, colorMode }: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((value) => !value), [])

  useEffect(() => {
    const container = containerRef.current
    if (!open || container === null) {
      return undefined
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !container.contains(event.target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // A click on the panel's own padding blurs to nothing; only focus landing outside closes.
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && !container.contains(event.relatedTarget)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    container.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      container.removeEventListener('focusout', onFocusOut)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={cn(
          'flex min-h-6 cursor-pointer items-center gap-1 rounded text-sm font-medium text-primary-main transition hover:underline',
          focusRing,
        )}
      >
        {name}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded border border-text-primary bg-background text-left shadow-card-hover"
        >
          {items.length > 0 && (
            <ul className="py-1.5">
              {items.map((item) => (
                <li key={item.label}>
                  <MenuEntry item={item} onDone={close} />
                </li>
              ))}
            </ul>
          )}

          {colorMode !== undefined && (
            <fieldset className="border-t border-divider pb-2 pt-2.5 first:border-t-0">
              <legend className="float-left mb-2 w-full px-4 text-sm text-text-secondary">
                {colorMode.label}
              </legend>
              <div className="clear-both flex px-2">
                <ModeTile icon={Sun} mode="light" label={colorMode.lightLabel} choice={colorMode} />
                <ModeTile icon={Moon} mode="dark" label={colorMode.darkLabel} choice={colorMode} />
              </div>
            </fieldset>
          )}
        </div>
      )}
    </div>
  )
}

/** One entry, which closes the menu once chosen. */
function MenuEntry(props: { readonly item: AccountMenuItem; readonly onDone: () => void }) {
  const { item, onDone } = props
  const onSelect = 'onSelect' in item ? item.onSelect : undefined
  const choose = useCallback(() => {
    onDone()
    onSelect?.()
  }, [onDone, onSelect])

  // A link opened in another tab or window leaves the menu, and its focus, where they are.
  const follow = useCallback(
    (event: MouseEvent) => {
      if (isPlainClick(event)) {
        onDone()
      }
    },
    [onDone],
  )

  return 'href' in item ? (
    <Link to={item.href} onClick={follow} className={itemClass}>
      {item.label}
    </Link>
  ) : (
    <button type="button" onClick={choose} className={itemClass}>
      {item.label}
    </button>
  )
}

/** One theme tile: icon over label, filled `primary-main` when chosen. */
function ModeTile(props: {
  readonly icon: IconComponent
  readonly mode: ColorMode
  readonly label: string
  readonly choice: ColorModeChoice
}) {
  const { icon: Icon, mode, choice } = props
  const active = choice.mode === mode
  const { onChange } = choice
  const choose = useCallback(() => onChange(mode), [onChange, mode])

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={choose}
      className={cn(
        'flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-lg py-3.5 text-sm font-medium transition',
        focusRing,
        active
          ? 'bg-primary-main text-primary-contrast'
          : 'bg-background text-text-primary hover:bg-background-contrast-sm',
      )}
    >
      <Icon size={20} />
      {props.label}
    </button>
  )
}
